import React from 'react';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useDocTitle } from '../components/CustomHook';
import { Link } from 'react-router-dom';

const AboutUs = () => {
    useDocTitle('About Us - TerraLucrum');

    return (
        <>
            <NavBar />
            <div className="bg-gray-50 min-h-screen pt-28">
                
                {/* Section 1: Who We Are */}
                <section className="min-h-screen flex flex-col">
                    {/* Hero Section - matching Process Improvement style */}
                    <div className="bg-terra-blue-900 text-white py-20">
                        <div className="container mx-auto px-4 text-center">
                            <h1 className="text-4xl lg:text-6xl font-bold mb-4">About Us</h1>
                            <p className="text-xl lg:text-2xl mb-8">Building lasting wealth through disciplined real estate investment</p>
                        </div>
                    </div>
                    
                    <div className="container mx-auto px-4 py-16 flex-1 flex items-center justify-center">

                        {/* Who We Are Content */}
                        <div className="max-w-4xl mx-auto text-center">
                            <h2 className="text-4xl font-bold text-gray-800 mb-8">Who We Are</h2>
                            <div className="w-24 h-1 bg-terra-blue-900 mx-auto mb-8"></div>
                            
                            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                                <p>
                                    TerraLucrum is a real estate investment firm that acquires, manages, and partners
                                    on income-producing property. We help investors build long-term wealth through
                                    disciplined underwriting and hands-on asset management.
                                </p>

                                <p>
                                    We specialize in identifying high-performing opportunities and operating our
                                    properties to maximize net operating income and durable, long-term value for our partners.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 2: Changing the Traditional Mindset / Our Philosophy */}
                <section className="min-h-screen flex items-center justify-center bg-white">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto text-center">
                            <h2 className="text-4xl font-bold text-gray-800 mb-8">A Smarter Approach to Real Estate Investing</h2>
                            <div className="w-24 h-1 bg-terra-blue-900 mx-auto mb-12"></div>

                            <div className="space-y-8 text-lg text-gray-700 leading-relaxed">

                               <p className="text-xl text-terra-blue-900 font-semibold">
                                    At TerraLucrum, we believe there is a better way to build wealth through real estate.
                                    Markets shift, and so should the traditional approach to acquiring, financing, and
                                    operating income-producing property.
                                </p>

                                <p>
                                    We pair rigorous underwriting with disciplined, hands-on asset management to protect
                                    our investors' capital and compound returns over the long term.
                                </p>
                            </div>
                            
                            {/* Values Grid */}
                            <div className="grid md:grid-cols-3 gap-8 mt-16">
                                <div className="bg-terra-blue-50 p-6 rounded-lg">
                                    <div className="text-terra-blue-900 mb-4">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 1L9 9l-8 0 6.5 4.7L5 22l7-5 7 5-2.5-8.3L23 9l-8 0z"/>
                                        </svg>
                                    </div>
                                    <h3 className="font-semibold text-lg text-gray-800 mb-2">Integrity & Trust</h3>
                                    <p className="text-gray-600">Protecting investor capital through transparency, honest reporting, and ethical stewardship</p>
                                </div>
                                
                                <div className="bg-terra-blue-50 p-6 rounded-lg">
                                    <div className="text-terra-blue-900 mb-4">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 1v6h6L8.5 5.5 10 4h5v2h-3l1 1 3.25 3.25c.31.31.69.5 1.06.69L18 12h4v8c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h6L8.5 2.5 4 1z"/>
                                        </svg>
                                    </div>
                                    <h3 className="font-semibold text-lg text-gray-800 mb-2">Partnership</h3>
                                    <p className="text-gray-600">Aligning our interests with our investors and partners to achieve shared, sustainable returns</p>
                                </div>
                                
                                <div className="bg-terra-blue-50 p-6 rounded-lg">
                                    <div className="text-terra-blue-900 mb-4">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                                        </svg>
                                    </div>
                                    <h3 className="font-semibold text-lg text-gray-800 mb-2">Long-Term Vision</h3>
                                    <p className="text-gray-600">Investing with patience and discipline to build lasting wealth across market cycles</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 3: Proven Track Record */}
                <section className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto text-center">
                            <h2 className="text-4xl font-bold text-terra-blue-900 mb-8">Proven Track Record of Success</h2>
                            <div className="w-24 h-1 bg-terra-blue-900 mx-auto mb-12"></div>

                            <div className="space-y-8 text-lg text-gray-700 leading-relaxed mb-16">
                                <p>
                                    TerraLucrum invests across a diverse range of income-producing property types and
                                    markets throughout the country.
                                </p>

                                <p>
                                    Our principals and core team bring decades of hands-on experience acquiring, financing,
                                    and operating real estate. That owner-operator perspective allows us to underwrite with
                                    discipline and build partnerships with our investors that are transparent and mutually rewarding.
                                </p>
                            </div>

                            {/* Property Types
                                    Multifamily
                                    Single-Family Rentals
                                    Commercial
                                    Retail
                                    Industrial
                                    Mixed-Use
                                    Land & Development
                                    Self-Storage
                                    Build-to-Rent
                                    Office
                                    Hospitality

                            */}
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-gray-800 mb-6">Property Types We Invest In</h3>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-left">
                                    <div className="flex items-center space-x-2">
                                        <span className="w-2 h-2 bg-terra-blue-900 rounded-full"></span>
                                        <span className="text-gray-700">Multifamily</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="w-2 h-2 bg-terra-blue-900 rounded-full"></span>
                                        <span className="text-gray-700">Single-Family Rentals</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="w-2 h-2 bg-terra-blue-900 rounded-full"></span>
                                        <span className="text-gray-700">Commercial</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="w-2 h-2 bg-terra-blue-900 rounded-full"></span>
                                        <span className="text-gray-700">Retail</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="w-2 h-2 bg-terra-blue-900 rounded-full"></span>
                                        <span className="text-gray-700">Industrial</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="w-2 h-2 bg-terra-blue-900 rounded-full"></span>
                                        <span className="text-gray-700">Mixed-Use</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="w-2 h-2 bg-terra-blue-900 rounded-full"></span>
                                        <span className="text-gray-700">Land & Development</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="w-2 h-2 bg-terra-blue-900 rounded-full"></span>
                                        <span className="text-gray-700">Self-Storage</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="w-2 h-2 bg-terra-blue-900 rounded-full"></span>
                                        <span className="text-gray-700">Build-to-Rent</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="w-2 h-2 bg-terra-blue-900 rounded-full"></span>
                                        <span className="text-gray-700">Office</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="w-2 h-2 bg-terra-blue-900 rounded-full"></span>
                                        <span className="text-gray-700">Hospitality</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 4: Call to Action */}
                <section className="flex items-center justify-center bg-white py-20">
                    <div className="container mx-auto px-4">
                        <div className="text-center max-w-4xl mx-auto">
                            <div className="bg-terra-blue-900 text-white p-8 rounded-lg">
                                <h3 className="text-2xl font-bold mb-4">Ready to Invest with TerraLucrum?</h3>
                                <p className="text-lg mb-6">Let's discuss how TerraLucrum can help you build long-term wealth through real estate investment.</p>
                                <Link to="/contact" className="bg-white text-terra-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                                    Contact Us
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <Footer />
        </>
    );
};

export default AboutUs;